// src/features/display/useDisplaySettings.js
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocale } from '../../contexts/LocaleContext';
import { useAppConfig } from '../../contexts/AppConfigContext';
import { useNotification } from '../../contexts/NotificationContext';
import { initialDisplaySettings } from '../../constants/display/initialDisplaySettings';
import { LOCAL_STORAGE_KEY_DISPLAY_SETTINGS } from '../../constants/display/localStorageKeys';
import { soundConfig } from '../../constants/display/soundConfig';
import { saveSoundEffect } from '../../services/db';
import { useDisplayAudio } from './useDisplayAudio';
import { useDisplaySpeech } from './useDisplaySpeech';
import { useDisplayTriggers } from './useDisplayTriggers';

export const useDisplaySettings = (
  structure,
  currentLevel,
  remainingTime,
  timerState,
  lastTimerUpdateTriggerType,
  lastP2PMessage,
  isIntegratedViewEnabled = false,
  isPrimaryDisplay = false
) => {
  const { t, locale } = useLocale();
  const { config: appConfig } = useAppConfig();
  const { showNotification } = useNotification();
  const [settings, setSettings] = useState(initialDisplaySettings);
  const isMounted = useRef(false);

  const firstActualLevelIndex = useMemo(() => {
    if (!structure || !structure.levels) return -1;
    return structure.levels.findIndex(level => level.type === 'level');
  }, [structure]);

  const handleSettingChange = useCallback((category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value,
      },
    }));
  }, []);

  // --- Sub-hooks ---
  const { isMutedByPrimary, getAudioContext, playSound, checkSoundExists } = useDisplayAudio(isPrimaryDisplay);

  const { speak } = useDisplaySpeech({
    settings,
    locale,
    isMutedByPrimary,
    showNotification,
    t,
    appConfig,
    handleSettingChange
  });

  useDisplayTriggers({
    structure,
    currentLevel,
    remainingTime,
    timerState,
    lastTimerUpdateTriggerType,
    settings,
    playSound,
    speak,
    isIntegratedViewEnabled,
    appConfig,
    firstActualLevelIndex
  });

  // --- Persistence ---
  useEffect(() => {
    if (!isMounted.current) {
      const saved = localStorage.getItem(LOCAL_STORAGE_KEY_DISPLAY_SETTINGS);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setSettings(prev => ({ ...prev, ...parsed }));
        } catch (e) {
          // Ignore
        }
      }
      isMounted.current = true;
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY_DISPLAY_SETTINGS, JSON.stringify(settings));
  }, [settings]);

  // --- Actions ---
  const setTheme = useCallback((themeName) => {
    setSettings(prev => ({ ...prev, currentThemeName: themeName }));
  }, []);

  const toggleQrCode = useCallback(() => {
    setSettings(prev => ({ ...prev, showQrCode: !prev.showQrCode }));
  }, []);

  const handleSoundFileSelect = async (category, file) => {
    if (!file) return;
    const { soundId } = soundConfig[category];
    try {
      await saveSoundEffect(soundId, file.name, file);
      handleSettingChange(category, 'selectedSound', file.name);
    } catch (error) {
      showNotification(t('errors.saveError'), 'error');
    }
  };

  return {
    settings,
    currentThemeName: settings.currentThemeName,
    actions: {
      handleSettingChange,
      handleSoundFileSelect,
      getAudioContext,
      playSound,
      checkSoundExists,
      speak,
      setTheme,
      toggleQrCode,
    },
  };
};